import React, { useState, useEffect } from 'react';
import {
   Dialog,
   DialogContent,
   DialogDescription,
   DialogFooter,
   DialogHeader,
   DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

const PLTCPPresetModal = ({ isOpen, onClose, onSave, initialPreset }) => {
   const defaultSelectedDocuments = {
      letterOfIntent: true,
      applicationLetter: false,
      lguEndorsement: false,
      homeownersResolution: false,
      ptaResolution: false
   };

   const [preset, setPreset] = useState({
      requestType: 'Cutting',
      name: '',
      address: '',
      contactNumber: '',
      treeType: [],
      treeStatus: [],
      landType: [],
      posingDanger: false,
      forPersonalUse: false,
      purpose: '',
      isWithinSubdivision: false,
      isSchoolOrOrganization: false,
      selectedDocuments: defaultSelectedDocuments
   });

   useEffect(() => {
      if (initialPreset) {
         setPreset({
            ...initialPreset,
            selectedDocuments: {
               ...defaultSelectedDocuments,
               ...(initialPreset.selectedDocuments || {})
            }
         });
      }
   }, [initialPreset]);

   const handleRequestTypeChange = (value) => {
      console.log('Changing request type to:', value);
      setPreset(prev => ({
         ...prev,
         requestType: value,
         selectedDocuments: value === 'Inventory' ? {
            letterOfIntent: true,
            applicationLetter: false,
            lguEndorsement: false,
            homeownersResolution: false,
            ptaResolution: false 
         } : defaultSelectedDocuments,
         ...(value === 'Inventory' && {
            isWithinSubdivision: false,
            isSchoolOrOrganization: false
         })
      }));
   };

   const handleCheckboxChange = (name, checked) => {
      if (preset.requestType === 'Inventory') return; // Prevent changes in Inventory mode

      setPreset(prev => ({
         ...prev,
         [name]: checked,
         selectedDocuments: {
            ...prev.selectedDocuments,
            homeownersResolution: name === 'isWithinSubdivision' ? checked : prev.selectedDocuments.homeownersResolution,
            ptaResolution: name === 'isSchoolOrOrganization' ? checked : prev.selectedDocuments.ptaResolution,
         }
      }));
   };

   const renderFileRequirements = () => {
      if (preset.requestType === 'Inventory') {
         return (
            <div key="letterOfIntent" className="flex items-center space-x-2 mb-4">
               <Checkbox
                  id="letterOfIntent"
                  checked={preset.selectedDocuments.letterOfIntent}
                  onCheckedChange={(checked) => {
                     setPreset(prev => ({
                        ...prev,
                        selectedDocuments: {
                           ...prev.selectedDocuments,
                           letterOfIntent: checked
                        }
                     }));
                  }}
               />
               <div>
                  <Label htmlFor="letterOfIntent" className="text-sm text-muted-foreground">Letter of Intent</Label>
                  <div className="mt-1 text-xs text-gray-500">Required</div>
               </div>
            </div>
         );
      }

      const fileTypes = [
         { type: 'applicationLetter', label: 'Application Letter', condition: true },
         { type: 'lguEndorsement', label: 'LGU Endorsement', condition: true },
         { type: 'homeownersResolution', label: 'Homeowners Resolution', condition: preset.isWithinSubdivision },
         { type: 'ptaResolution', label: 'PTA Resolution', condition: preset.isSchoolOrOrganization }
      ];

      return fileTypes
         .filter(({ condition }) => condition)
         .map(({ type, label }) => (
            <div key={type} className="flex items-center space-x-2 mb-4">
               <Checkbox
                  id={type}
                  checked={preset.selectedDocuments[type]}
                  onCheckedChange={(checked) => {
                     setPreset(prev => ({
                        ...prev,
                        selectedDocuments: {
                           ...prev.selectedDocuments,
                           [type]: checked
                        }
                     }));
                  }}
               />
               <div>
                  <Label htmlFor={type} className="text-sm text-muted-foreground">{label}</Label>
                  <div className="mt-1 text-xs text-gray-500">
                     {type === 'homeownersResolution' || type === 'ptaResolution' ? 'Conditional' : 'Required'}
                  </div>
               </div>
            </div>
         ));
   };

   const handleSave = () => {
      const presetToSave = {
         ...preset,
         requestType: preset.requestType,
         selectedDocuments: preset.selectedDocuments
      };
      console.log('Saving PLTCP preset:', presetToSave);
      localStorage.setItem('pltcpPreset', JSON.stringify(presetToSave));
      onSave(presetToSave);
      onClose();
      toast.success('PLTCP preset saved successfully');
   };

   return (
      <Dialog open={isOpen} onOpenChange={onClose}>
         <DialogContent className="max-w-[600px] max-h-[80vh]">
            <DialogHeader>
               <DialogTitle>Edit PLTCP Application Preset</DialogTitle>
               <DialogDescription>
                  Configure default values for test PLTCP applications
               </DialogDescription>
            </DialogHeader>

            <ScrollArea className="h-[500px] pr-4">
               <div className="space-y-4 pb-4">
                  <div>
                     <Label className="text-base">Request Type</Label>
                     <RadioGroup
                        value={preset.requestType}
                        onValueChange={handleRequestTypeChange}
                        className="mt-2 space-y-2"
                     >
                        <div className="flex items-center space-x-2">
                           <RadioGroupItem value="Cutting" id="cutting" />
                           <Label htmlFor="cutting">Cutting</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                           <RadioGroupItem value="Inventory" id="inventory" />
                           <Label htmlFor="inventory">Inventory</Label>
                        </div>
                     </RadioGroup>
                  </div>

                  {preset.requestType === 'Cutting' && (
                     <div className="space-y-2">
                        <Label className="text-base">Additional Information</Label>
                        <div className="space-y-2">
                           <div className="flex items-center space-x-2">
                              <Checkbox
                                 id="isWithinSubdivision"
                                 checked={preset.isWithinSubdivision}
                                 onCheckedChange={(checked) => handleCheckboxChange('isWithinSubdivision', checked)}
                              />
                              <Label htmlFor="isWithinSubdivision">Is Within Subdivision</Label>
                           </div>
                           <div className="flex items-center space-x-2">
                              <Checkbox
                                 id="isSchoolOrOrganization"
                                 checked={preset.isSchoolOrOrganization}
                                 onCheckedChange={(checked) => handleCheckboxChange('isSchoolOrOrganization', checked)}
                              />
                              <Label htmlFor="isSchoolOrOrganization">Is School/Organization</Label>
                           </div>
                        </div>
                     </div>
                  )}

                  <div className="border-t border-gray-200 pt-4 mt-4">
                     <h3 className="text-lg font-semibold mb-4">Required Documents</h3>
                     {renderFileRequirements()}
                  </div>
               </div>
            </ScrollArea>

            <DialogFooter>
               <Button variant="outline" onClick={onClose}>
                  Cancel
               </Button>
               <Button onClick={handleSave}>Save Preset</Button>
            </DialogFooter>
         </DialogContent>
      </Dialog>
   );
};

export default PLTCPPresetModal;